import {ReactElement, useMemo} from "react";
import {compact, isEmpty} from "lodash";

import {IconBankruptcy, IconCallCenter, IconCog, IconDSC, IconPartner} from "assets";
import {IconInventory, IconKey, IconLegalCollections, IconSearchBig} from "@bounce/shared/ui/assets";
import {urls} from "../../routes/urls";
import {Colors, IconApproved, IconCompliance, MenuListItem} from "@bounce/shared/ui";
import {usePermissions} from "../../hooks/usePermissions";
import {PermissionKey} from "@bounce/shared/enums/permissions";
import {hasPermission} from "@bounce/shared/helpers/permissionUtils";
import {env} from "@bounce/shared";

type MenuItemConfig = {
  kind: "item";
  id: string;
  title: string;
  icon: ReactElement;
  url: string;
  permission?: PermissionKey;
};

type MenuGroupConfig = {
  kind: "nest";
  id: string;
  title: string;
  icon: ReactElement;
  permission?: PermissionKey;
  items: {
    id: string;
    title: string;
    url: string;
    permission?: PermissionKey;
  }[];
};

type MenuConfig = MenuItemConfig | MenuGroupConfig;

const filterByPermission = <T extends {permission?: PermissionKey}>(items: T[], can: (p: PermissionKey) => boolean) =>
  items.filter((i) => !i.permission || can(i.permission));

export const useSpringMenuItems = (): MenuListItem[] => {
  const permissions = usePermissions();

  const can = useMemo(() => {
    return (p: PermissionKey) => hasPermission(permissions ?? [], p);
  }, [permissions]);

  const MENU: MenuConfig[] = [
    {
      kind: "item",
      id: "search",
      title: "search",
      icon: <IconSearchBig color={Colors.primary._400} width={20} height={20} />,
      url: urls.searchSpring.url,
    },
    {
      kind: "nest",
      id: "operations",
      title: "operations",
      icon: <IconKey color={Colors.primary._400} size={20} />,
      items: [
        {
          id: "account-imports",
          title: "Account Imports",
          url: urls.accountImports.url,
          permission: PermissionKey.SPRING_OPERATIONS_ACCOUNT_IMPORTS,
        },
        {
          id: "account-management",
          title: "Account Management",
          url: urls.accountManagement.url,
          permission: PermissionKey.SPRING_OPERATIONS_ACCOUNT_MANAGEMENT,
        },
        {
          id: "import-payments",
          title: "payments",
          url: urls.importPayments.url,
          permission: PermissionKey.SPRING_OPERATIONS_PAYMENTS,
        },
        {
          id: "washing",
          title: "washing",
          url: urls.washing.url,
          permission: PermissionKey.SPRING_OPERATIONS_WASHING,
        },
        {
          id: "user-level-media",
          title: "User-level Media",
          url: urls.userLevelMedia.url,
          permission: PermissionKey.SPRING_OPERATIONS_USER_LEVEL_MEDIA,
        },
        {
          id: "batch-level-media",
          title: "Batch-level Media",
          url: urls.batchLevelMedia.url,
          permission: PermissionKey.SPRING_OPERATIONS_BATCH_LEVEL_MEDIA,
        },
        {
          id: "import-pricing",
          title: "pricing - Import",
          url: urls.importPricing.url,
          permission: PermissionKey.SPRING_OPERATIONS_PRICING_IMPORT,
        },
        {
          id: "generate-pricing",
          title: "pricing - Batch",
          url: urls.generatePricing.url,
          permission: PermissionKey.SPRING_OPERATIONS_PRICING_BATCH,
        },
      ],
    },
    {
      kind: "nest",
      id: "debt-settlement",
      title: "debt settlement",
      icon: <IconDSC color={Colors.primary._400} size={20} />,
      items: [
        {
          id: "overview",
          title: "overview",
          url: urls.dscOverview.url,
          permission: PermissionKey.SPRING_DEBT_SETTLEMENT_OVERVIEW,
        },
        {
          id: "bulk-operations",
          title: "bulk operations",
          url: urls.dscBulkOperations.url,
          permission: PermissionKey.SPRING_DEBT_SETTLEMENT_BULK_OPERATIONS,
        },
        {
          id: "anonymous-communications",
          title: "anonymous comms",
          url: urls.anonymousCommunications.url,
          permission: PermissionKey.SPRING_DEBT_SETTLEMENT_ANONYMOUS_COMMS,
        },
        {
          id: "representation-companies",
          title: "companies",
          url: urls.representationCompanies.url,
          permission: PermissionKey.SPRING_DEBT_SETTLEMENT_COMPANIES,
        },
      ],
    },
    {
      kind: "item",
      id: "inventory",
      title: "inventory",
      icon: <IconInventory color={Colors.primary._400} width={20} height={20} />,
      url: urls.inventory.url,
      permission: PermissionKey.SPRING_INVENTORY,
    },
    {
      kind: "nest",
      id: "compliance",
      title: "Compliance",
      icon: <IconCompliance color={Colors.primary._400} width={20} height={20} />,
      permission: PermissionKey.SPRING_COMPLIANCE,
      items: [
        {
          id: "disputes-dashboard",
          title: "Overview",
          url: urls.disputesDashboard.url,
          permission: PermissionKey.SPRING_COMPLIANCE_OVERVIEW,
        },
        {
          id: "disputes-table",
          title: "Disputes",
          url: urls.disputes.url,
          permission: PermissionKey.SPRING_COMPLIANCE_DISPUTES,
        },
        {
          id: "document-export",
          title: "Document export",
          url: urls.documentExport.url,
          permission: PermissionKey.SPRING_COMPLIANCE_DOCUMENT_EXPORT,
        },
      ],
    },
    {
      kind: "item",
      id: "bankruptcies",
      title: "bankruptcies",
      icon: <IconBankruptcy color={Colors.primary._400} size={20} />,
      url: urls.bankruptcies.url,
      permission: PermissionKey.SPRING_BANKRUPTCIES,
    },
    {
      kind: "nest",
      id: "legal-collections",
      title: "Legal Collections",
      icon: <IconLegalCollections color={Colors.primary._400} size={20} />,
      items: [
        {
          id: "legal-collections-import",
          title: "Bulk operations",
          url: urls.legalCollectionsBulkOperation.url,
          permission: PermissionKey.SPRING_LEGAL_COLLECTIONS_BULK_OPERATIONS,
        },
      ],
    },
    {
      kind: "nest",
      id: "partner-platform",
      title: "Partner Platform",
      icon: <IconPartner color={Colors.primary._400} size={20} />,
      permission: PermissionKey.SPRING_PARTNER_MANAGEMENT,
      items: compact([
        {id: "partners", title: "Manage partners", url: urls.partners.url},
        {id: "payments-credentials", title: "Payments Credentials", url: urls.paymentsCredentials.url},
        {id: "manage-partner-users", title: "Manage partner users", url: urls.managePartnerUsers.url},
        {id: "partner-bulk-operations", title: "Bulk operations", url: urls.partnerPlatformBulkOperation.url},
        {id: "reports", title: "Reports", url: urls.partnerPlatformReports.url},
        {id: "document-requests", title: "Document requests", url: urls.documentRequests.url},
      ]),
    },
    {
      kind: "nest",
      id: "call-center",
      title: "call center",
      icon: <IconCallCenter color={Colors.primary._400} size={20} />,
      items: compact([
        {
          id: "shift-management",
          title: "Shift management",
          url: urls.shiftManagement.url,
          permission: PermissionKey.SPRING_CALL_CENTER_SHIFT_MANAGEMENT,
        },
        {
          id: "conversations",
          title: "conversations",
          url: urls.conversationsSpring.url,
          permission: PermissionKey.SPRING_CALL_CENTER_CONVERSATIONS,
        },
        {
          id: "blocked-numbers",
          title: "Blocked numbers",
          url: urls.blockedNumbers.url,
          permission: PermissionKey.SPRING_CALL_CENTER_BLOCKED_NUMBERS,
        },
        {
          id: "employees",
          title: "Employees",
          url: urls.rexEmployees.url,
          permission: PermissionKey.SPRING_CALL_CENTER_REX_SKILLS,
        },
        ["local", "staging"].includes(env) && {
          id: "simulation",
          title: "Simulation",
          url: urls.simulation.url,
        },
      ]),
    },
    {
      kind: "item",
      id: "tasks",
      title: "Tasks",
      icon: <IconApproved color={Colors.primary._400} width={20} height={20} />,
      url: urls.tasks.url,
      permission: PermissionKey.SPRING_TASKS,
    },
    {
      kind: "nest",
      id: "admin",
      title: "Admin",
      icon: <IconCog color={Colors.primary._400} width={20} height={20} />,
      items: [
        {id: "roles", title: "Roles", url: urls.roles.url, permission: PermissionKey.SPRING_ADMIN_ROLES},
        {id: "people", title: "People", url: urls.people.url, permission: PermissionKey.SPRING_ADMIN_PEOPLE},
        {
          id: "sftp-management",
          title: "SFTP Management",
          url: urls.sftpManagement.url,
          permission: PermissionKey.SPRING_ADMIN_SFTP_MANAGEMENT,
        },
      ],
    },
    {
      kind: "nest",
      id: "gr4vy",
      title: "Gr4vy",
      icon: <IconApproved color={Colors.primary._400} width={20} height={20} />,
      items: [
        {
          id: "gr4vy-validate-logic",
          title: "Validate Logic",
          url: urls.gr4vyValidateLogic.url,
          permission: PermissionKey.SPRING_GR4VY_ROUTING_VALIDATION,
        },
      ],
    },
  ];

  return useMemo(() => {
    return compact(
      MENU.map((node) => {
        if (node.kind === "item") {
          if (node.permission && !can(node.permission)) return null;

          return {
            id: node.id,
            type: "category-item" as const,
            title: node.title,
            icon: node.icon,
            url: node.url,
            display: true,
          } satisfies MenuListItem;
        }

        // nest
        if (node.permission && !can(node.permission)) return null;

        const subListItems = filterByPermission(node.items, can).map((i) => ({
          id: i.id,
          title: i.title,
          url: i.url,
          display: true,
        }));

        if (isEmpty(subListItems)) return null;

        return {
          id: node.id,
          type: "category-nest-item" as const,
          title: node.title,
          icon: node.icon,
          subListItems,
          display: true,
        } satisfies MenuListItem;
      })
    );
  }, [can]);
};
